import { gql } from '@apollo/client';
import { AddIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Center,
  Heading,
  HStack,
  Input,
  Spacer,
  Spinner,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import debounce from 'lodash/debounce';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useAuthentication } from '~auth/useAuthentication';
import AppLayout from '~components/ui/AppLayout';
import Breadcrumb from '~components/ui/Breadcrumb';
import { DownloadIconBlue } from '~components/ui/icons';
import Oops from '~components/ui/Oops';
import { ZteRowFragment } from '~components/zte/__generated__/ZteTable.graphql';
import { useZteTable } from '~components/zte/useZteTable';
import { ZteCSVUploadModal } from '~components/zte/ZteCSVUploadModal';
import { ZteDetailsModal } from '~components/zte/ZteDetailsModal';
import { ZteTable } from '~components/zte/ZteTable';
import { useQueryParams } from '../UseQueryParams';
import { useZteBatchesQuery } from './__generated__/Zte.graphql';

function ZtePage() {
  const { user } = useAuthentication();
  const userRoles = user['https://wave.ppds.com/user_roles'] ?? [];
  const hasZteAdminpermission = userRoles.includes('ZteAdmin');

  const toast = useToast();
  const { loading, error, data, refetch } = useZteBatchesQuery();
  const { isOpen: isCreateOpen, onOpen: createOnOpen, onClose: createOnClose } = useDisclosure();
  const queryParams = useQueryParams();
  const [search, setSearch] = React.useState(queryParams.get('search') || '');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBatchRow, setSelectedBatchRow] = useState<ZteRowFragment | null>(null);
  const [refreshingRows, setRefreshingRows] = useState<Set<string>>(new Set());
  const handleBatchNameClick = useCallback((row: ZteRowFragment) => {
    setSelectedBatchRow(row);
    setIsModalOpen(true);
  }, []);
  const debouncedSearch = useRef(
    debounce(async (newSearch: string) => {
      history.replaceState(
        {},
        window.document.title,
        newSearch ? `?search=${newSearch}` : window.location.pathname,
      );
      setSearch(newSearch);
    }, 100),
  ).current;

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSearch(event.target.value);
  };

  const handleRefreshRow = useCallback(
    async (id: string) => {
      setRefreshingRows((prev) => new Set(prev).add(id));
      try {
        await refetch();
      } finally {
        setRefreshingRows((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    },
    [refetch],
  );

  const handleCreateOnSuccess = useCallback(() => {
    refetch();
    createOnClose();
    toast({
      title: 'Upload Successful',
      description: `Your CSV file has been uploaded.`,
      status: 'success',
    });
  }, [createOnClose, refetch, toast]);

  const table = useZteTable(
    useMemo(() => {
      const normalizedSearch = search.toLowerCase();
      const trimmedSearch = search.trim();

      const isValidSearch = trimmedSearch.length >= 3;

      const searchMatch = (...args: string[]) =>
        args.some((str) => str.toLowerCase().includes(normalizedSearch));

      return {
        data:
          (isValidSearch
            ? data?.zteBatches.filter((zte: ZteRowFragment) =>
                searchMatch(
                  zte.batchName,
                  zte.fileName,
                  zte.createdBy ?? '',
                  zte.organizationName ?? '',
                  zte.customerName ?? '',
                  zte.status,
                ),
              )
            : data?.zteBatches) || [],
        onBatchNameClick: handleBatchNameClick,
        onRefreshRow: handleRefreshRow,
        refreshingRows,
      };
    }, [search, data?.zteBatches, handleBatchNameClick, handleRefreshRow, refreshingRows]),
  );

  return (
    <>
      <Helmet>
        <title>Zte</title>
      </Helmet>
      <>
        <AppLayout.FixedContent>
          <Box px="16" py="4" bg="gray.200">
            <Breadcrumb>
              <Breadcrumb.Item>TP Vision</Breadcrumb.Item>
              <Breadcrumb.ItemLink to={`/zte`}>Zte</Breadcrumb.ItemLink>
            </Breadcrumb>
          </Box>
        </AppLayout.FixedContent>
        <AppLayout.ScrollableContent ml="8">
          <HStack alignItems="center" mt="4" spacing="5">
            <Heading>Zero Touch Enrolment</Heading>
            {hasZteAdminpermission && (
              <Box>
                <Button
                  leftIcon={<AddIcon />}
                  color={'white'}
                  backgroundColor={'blue.600'}
                  size="xs"
                  p={4}
                  variant="outline"
                  _hover={{ backgroundColor: 'blue.600' }}
                  onClick={() => createOnOpen()}
                >
                  Upload new CSV file
                </Button>
              </Box>
            )}
            <Spacer />
            <Box>
              <Button
                as="a"
                href="/CSV_TEMPLATE.csv"
                download
                leftIcon={<DownloadIconBlue boxSize="14px" />}
                color="blue.600"
                backgroundColor="white"
                size="xs"
                p={4}
                variant="outline"
                _hover={{ backgroundColor: 'white' }}
              >
                Download CSV template
              </Button>
            </Box>
            <Box pr={10}>
              <Input
                id="search"
                type="text"
                onChange={handleSearch}
                placeholder="Search"
                defaultValue={search}
              />
            </Box>
          </HStack>

          <Box marginTop="10">
            {loading && (
              <Center flex={1} pt={15}>
                <Spinner boxSize="80px" thickness="8px" color="#627D98" />
              </Center>
            )}
            {error && <Oops error={error} />}
            {data && <ZteTable table={table} />}
          </Box>
        </AppLayout.ScrollableContent>
        <ZteCSVUploadModal
          isOpen={isCreateOpen}
          onClose={createOnClose}
          onSuccess={handleCreateOnSuccess}
        />
        {selectedBatchRow && (
          <ZteDetailsModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            row={selectedBatchRow}
          />
        )}
      </>
    </>
  );
}

ZtePage.graphql = {
  queries: {
    zteBatches: gql`
      query zteBatches {
        zteBatches {
          ...ZteRow
        }
      }
    `,
  },
};

export default ZtePage;
